import type { PlayerCri, SortKey } from '../types/neuro'

export type StatusTone = 'ready' | 'moderate' | 'low' | 'neutral'

export const toneClasses: Record<
  StatusTone,
  { text: string; bg: string; dot: string; border: string }
> = {
  ready: {
    text: 'text-[#15803D]',
    bg: 'bg-[#DCFCE7]',
    dot: 'bg-[#15803D]',
    border: 'border-[#BBF7D0]',
  },
  moderate: {
    text: 'text-[#B45309]',
    bg: 'bg-[#FEF3C7]',
    dot: 'bg-[#B45309]',
    border: 'border-[#FDE68A]',
  },
  low: {
    text: 'text-[#B91C1C]',
    bg: 'bg-[#FEE2E2]',
    dot: 'bg-[#B91C1C]',
    border: 'border-[#FECACA]',
  },
  neutral: {
    text: 'text-[#6B7280]',
    bg: 'bg-[#F3F4F6]',
    dot: 'bg-[#6B7280]',
    border: 'border-[#E5E7EB]',
  },
}

export const criTone = (cri: number): StatusTone =>
  cri >= 70 ? 'ready' : cri >= 50 ? 'moderate' : 'low'

export const criLabel: Record<StatusTone, string> = {
  ready: 'Ready',
  moderate: 'Moderate',
  low: 'Low',
  neutral: 'Neutral',
}

export const isLimitedSample = (player: PlayerCri) => player.confidence < 0.75

export type StatusIconKey = 'zap' | 'refresh' | 'alert' | 'minus'

export interface StatusLine {
  tone: StatusTone
  icon: StatusIconKey
  text: string
}

/** First matching rule wins, in the order defined by the product spec. */
export const statusLine = (player: PlayerCri): StatusLine => {
  if (player.rei_match >= 85)
    return { tone: 'ready', icon: 'zap', text: 'Very high cognitive readiness' }
  if (player.rei_match <= 20)
    return {
      tone: 'low',
      icon: 'refresh',
      text: 'Difficulty recovering from errors',
    }
  if (isLimitedSample(player))
    return { tone: 'moderate', icon: 'alert', text: 'Limited sample' }
  return { tone: 'neutral', icon: 'minus', text: 'Within normal range' }
}

export const interpretation = (player: PlayerCri): string => {
  const line = statusLine(player)
  if (line.icon === 'zap' || line.icon === 'refresh') return line.text
  const tone = criTone(player.cri_match)
  if (tone === 'ready') return 'Ready to perform at a high cognitive level'
  if (tone === 'moderate') return 'Moderate readiness, with some lapses'
  return 'Low readiness for most of the game'
}

export const sortPlayers = (players: PlayerCri[], key: SortKey) => {
  const sorted = [...players]
  switch (key) {
    case 'minutes':
      return sorted.sort((a, b) => b.minutes_played - a.minutes_played)
    case 'focus':
      return sorted.sort((a, b) => b.fi_match - a.fi_match)
    case 'impulsivity':
      return sorted.sort((a, b) => a.ii_match - b.ii_match)
    case 'reaction':
      return sorted.sort((a, b) => b.rei_match - a.rei_match)
    default:
      return sorted.sort((a, b) => b.cri_match - a.cri_match)
  }
}

export const initials = (name: string) =>
  name
    .replace(/\./g, '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

export const formatMeta = (player: PlayerCri) =>
  `${player.minutes_played.toFixed(1)} min · ${player.n_player_events} events`
